import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { fetchMySubscription } from '../../store/actions/SubscriptionActions';
import SubscriptionService from '../../services/SubscriptionService';
import { Card, Button, Badge } from '../ui';

const SubscriptionSuccess = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const dispatch = useDispatch();
  const [verifying, setVerifying] = useState(true);
  const [error, setError] = useState(null);
  const [session, setSession] = useState(null);
  const { loading } = useSelector((s) => s.subscription);
  const sessionId = searchParams.get('session_id');

  useEffect(() => {
    if (!sessionId) {
      setError('No session ID found. Please contact support.');
      setVerifying(false);
      return;
    }
    (async () => {
      try {
        const res = await SubscriptionService.verifyCheckoutSession(sessionId);
        setSession(res.session);
        await dispatch(fetchMySubscription());
        localStorage.setItem('hasSubscription', 'true');
        if (window.refreshSubscriptionStatus) await window.refreshSubscriptionStatus();
      } catch {
        setError('Failed to verify your subscription. Please contact support.');
      } finally {
        setVerifying(false);
      }
    })();
  }, [sessionId, dispatch]);

  if (verifying || loading) {
    return (
      <div className="tw-mx-auto tw-max-w-md tw-py-16 tw-text-center">
        <i className="fa-solid fa-spinner fa-spin tw-text-3xl tw-text-brand" />
        <h1 className="tw-mt-4 tw-text-xl tw-font-bold tw-text-heading">Verifying your subscription…</h1>
        <p className="tw-text-content-muted">Confirming your payment with Stripe.</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="tw-mx-auto tw-max-w-md tw-py-12">
        <Card className="tw-text-center">
          <div className="tw-mx-auto tw-mb-4 tw-flex tw-h-14 tw-w-14 tw-items-center tw-justify-center tw-rounded-2xl tw-bg-live/15 tw-text-xl tw-text-live">
            <i className="fa-solid fa-circle-exclamation" />
          </div>
          <h1 className="tw-text-lg tw-font-bold tw-text-heading">Verification failed</h1>
          <p className="tw-mt-2 tw-text-content-muted">{error}</p>
          <Button className="tw-mt-4" onClick={() => navigate('/billing')}>Go to Billing</Button>
        </Card>
      </div>
    );
  }

  return (
    <div className="tw-mx-auto tw-max-w-2xl tw-py-8">
      <Card className="tw-text-center">
        <div className="tw-mx-auto tw-mb-4 tw-flex tw-h-16 tw-w-16 tw-items-center tw-justify-center tw-rounded-2xl tw-bg-good/15 tw-text-2xl tw-text-good">
          <i className="fa-solid fa-circle-check" />
        </div>
        <h1 className="tw-text-xl tw-font-bold tw-text-heading">Payment successful</h1>
        <p className="tw-mt-2 tw-text-content-muted">Your subscription is now active. Time to start broadcasting.</p>

        {session && (
          <div className="tw-mx-auto tw-mt-5 tw-grid tw-max-w-md tw-grid-cols-2 tw-gap-3 tw-rounded-xl tw-border tw-border-line tw-bg-ink-850 tw-p-4 tw-text-left tw-text-sm">
            <div><div className="tw-text-content-faint">Plan</div><div className="tw-font-semibold tw-text-brand">{session.plan_name}</div></div>
            <div><div className="tw-text-content-faint">Amount</div><div className="tw-font-semibold tw-text-heading">${(session.amount_total / 100).toFixed(2)} {session.currency?.toUpperCase()}</div></div>
            <div><div className="tw-text-content-faint">Billing</div><div className="tw-font-semibold tw-text-heading">{session.billing_cycle === 'yearly' ? 'Yearly' : 'Monthly'}</div></div>
            <div><div className="tw-text-content-faint">Status</div><Badge tone="good">Active</Badge></div>
          </div>
        )}

        <div className="tw-mt-6 tw-flex tw-flex-wrap tw-justify-center tw-gap-3">
          <Button variant="secondary" icon="fa-house" onClick={() => { window.location.href = '/stream-dashboard'; }}>Go to Dashboard</Button>
          <Button icon="fa-arrow-right" onClick={() => navigate('/billing')}>Manage Subscription</Button>
        </div>
      </Card>

      <Card title="What's next" className="tw-mt-6">
        <div className="tw-grid tw-gap-4 sm:tw-grid-cols-3">
          {[
            ['1', 'Create streams', 'Spin up live streams up to your plan limit'],
            ['2', 'Upload VOD', 'Build a media library for playlists & failover'],
            ['3', 'Share', 'Give your audience the HLS / DASH links'],
          ].map(([n, t, d]) => (
            <div key={n} className="tw-flex tw-gap-3">
              <span className="tw-flex tw-h-7 tw-w-7 tw-flex-none tw-items-center tw-justify-center tw-rounded-full tw-bg-brand-soft tw-text-sm tw-font-bold tw-text-brand">{n}</span>
              <div><h4 className="tw-text-sm tw-font-semibold tw-text-heading">{t}</h4><p className="tw-text-xs tw-text-content-muted">{d}</p></div>
            </div>
          ))}
        </div>
      </Card>
    </div>
  );
};

export default SubscriptionSuccess;
